import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/** Full pickup window (host portal – includes all fields) */
export interface PickupWindow {
  id: string;
  producer_id: string;
  pickup_date: string;
  pickup_start: string;
  pickup_end: string;
  order_deadline_at: string | null;
  status: 'active' | 'closed' | 'cancelled';
  capacity: number | null;
  note: string | null;
  pickup_instructions: string | null;
  created_at: string;
  updated_at: string;
}

/** Public-facing pickup window (customer checkout – minimal fields) */
export interface PublicPickupWindow {
  id: string;
  producer_id: string;
  pickup_date: string;
  pickup_start: string;
  pickup_end: string;
  order_deadline_at: string | null;
  status: 'active' | 'closed' | 'cancelled';
}

export interface PickupWindowInput {
  producer_id: string;
  pickup_date: string;
  pickup_start: string;
  pickup_end: string;
  order_deadline_at?: string | null;
  capacity?: number | null;
  note?: string | null;
  pickup_instructions?: string | null;
}

/**
 * Fetch pickup windows for a producer (host portal – all statuses).
 */
export function usePickupWindows(producerId: string | undefined) {
  return useQuery({
    queryKey: ['pickup-windows', producerId],
    queryFn: async () => {
      if (!producerId) return [];
      const { data, error } = await supabase
        .from('pickup_windows' as any)
        .select('*')
        .eq('producer_id', producerId)
        .order('pickup_date', { ascending: true })
        .order('pickup_start', { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as PickupWindow[];
    },
    enabled: !!producerId,
  });
}

/**
 * Fetch active, upcoming pickup windows for a producer (customer checkout).
 * Uses explicit column list – note is NOT exposed publicly.
 */
export function useActivePickupWindows(producerId: string | undefined) {
  return useQuery({
    queryKey: ['pickup-windows-active', producerId],
    queryFn: async () => {
      if (!producerId) return [];
      const today = new Date().toISOString().slice(0, 10);
      const { data, error } = await supabase
        .from('pickup_windows' as any)
        .select('id, producer_id, pickup_date, pickup_start, pickup_end, order_deadline_at, status')
        .eq('producer_id', producerId)
        .eq('status', 'active')
        .gte('pickup_date', today)
        .order('pickup_date', { ascending: true })
        .order('pickup_start', { ascending: true });
      if (error) throw error;
      // Filter out windows past deadline
      const now = new Date();
      return ((data || []) as unknown as PublicPickupWindow[]).filter(w => {
        if (!w.order_deadline_at) return true;
        return new Date(w.order_deadline_at) > now;
      });
    },
    enabled: !!producerId,
  });
}

/**
 * Check if a producer has ever created any pickup windows (any status).
 * Uses SECURITY DEFINER RPC to bypass RLS date filter.
 * Used to distinguish "never set up" from "all expired".
 */
export function useHasPickupWindows(producerId: string | undefined) {
  return useQuery({
    queryKey: ['pickup-windows-exists', producerId],
    queryFn: async () => {
      if (!producerId) return false;
      const { data, error } = await supabase.rpc('producer_has_pickup_windows', {
        p_producer_id: producerId,
      });
      if (error) throw error;
      return !!data;
    },
    enabled: !!producerId,
  });
}

export function useCreatePickupWindow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PickupWindowInput) => {
      const { data, error } = await supabase
        .from('pickup_windows' as any)
        .insert(input as any)
        .select()
        .single();
      if (error) throw error;
      return data as unknown as PickupWindow;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['pickup-windows', variables.producer_id] });
    },
  });
}

export function useUpdatePickupWindowStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, producerId }: { id: string; status: string; producerId: string }) => {
      const { error } = await supabase
        .from('pickup_windows' as any)
        .update({ status } as any)
        .eq('id', id);
      if (error) throw error;
      return { id, status, producerId };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['pickup-windows', data.producerId] });
    },
  });
}

export interface PickupWindowUpdateInput {
  id: string;
  producer_id: string;
  order_deadline_at?: string | null;
  note?: string | null;
  capacity?: number | null;
  pickup_instructions?: string | null;
  // Structural fields – only allowed when no orders exist (server-enforced)
  pickup_date?: string;
  pickup_start?: string;
  pickup_end?: string;
}

export function useUpdatePickupWindow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PickupWindowUpdateInput) => {
      const { id, producer_id, ...fields } = input;
      const { data, error } = await supabase
        .from('pickup_windows' as any)
        .update(fields as any)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return { data, producer_id };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['pickup-windows', result.producer_id] });
    },
  });
}

/** Raw row from get_pack_list RPC (one row per order-item) */
export interface PackListDetailRow {
  product_id: string;
  product_title: string;
  quantity: number;
  preview_image_url: string | null;
  order_id: string;
  order_number: string;
  customer_name: string;
  customer_email: string;
  picked_up_at: string | null;
  paid_at: string | null;
  paid_method: string | null;
}

/** Aggregated product with customer breakdown */
export interface PackListItem {
  product_id: string;
  product_title: string;
  total_quantity: number;
  preview_image_url: string | null;
  customers: {
    order_id: string;
    order_number: string;
    customer_label: string;
    quantity: number;
    picked_up_at: string | null;
    paid_at: string | null;
    paid_method: string | null;
  }[];
}

function aggregatePackList(rows: PackListDetailRow[]): PackListItem[] {
  const map = new Map<string, PackListItem>();
  for (const r of rows) {
    let item = map.get(r.product_id);
    if (!item) {
      item = {
        product_id: r.product_id,
        product_title: r.product_title,
        total_quantity: 0,
        preview_image_url: r.preview_image_url,
        customers: [],
      };
      map.set(r.product_id, item);
    }
    item.total_quantity += r.quantity;
    const label = r.customer_name?.trim() || r.customer_email || `Beställning ${r.order_number}`;
    item.customers.push({
      order_id: r.order_id,
      order_number: r.order_number,
      customer_label: label,
      quantity: r.quantity,
      picked_up_at: r.picked_up_at,
      paid_at: r.paid_at,
      paid_method: r.paid_method,
    });
  }
  return Array.from(map.values());
}

/** Mark or unmark an order as picked up */
export function useMarkPickedUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, undo = false }: { orderId: string; undo?: boolean }) => {
      const { error } = await supabase
        .from('orders')
        .update({ picked_up_at: undo ? null : new Date().toISOString() } as any)
        .eq('id', orderId);
      if (error) {
        console.error('[useMarkPickedUp] error:', error.code, error.message);
        throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pack-list'] });
    },
  });
}

export function usePackList(pickupWindowId: string | undefined) {
  return useQuery({
    queryKey: ['pack-list', pickupWindowId],
    queryFn: async () => {
      if (!pickupWindowId) return [];
      const { data, error } = await supabase.rpc('get_pack_list', {
        p_pickup_window_id: pickupWindowId,
      });
      if (error) {
        console.error('[usePackList] RPC error:', error.code, error.message, { pickupWindowId });
        throw error;
      }
      return aggregatePackList((data || []) as unknown as PackListDetailRow[]);
    },
    enabled: !!pickupWindowId,
  });
}

/** Order count per pickup window – lightweight query for pack list day tabs */
export interface WindowOrderCount {
  pickup_window_id: string;
  order_count: number;
  total_items: number;
}

export function useWindowOrderCounts(windowIds: string[]) {
  return useQuery({
    queryKey: ['window-order-counts', windowIds],
    queryFn: async () => {
      if (windowIds.length === 0) return [] as WindowOrderCount[];
      const { data, error } = await supabase
        .from('orders')
        .select('pickup_window_id, id, order_items(quantity)')
        .in('pickup_window_id', windowIds)
        .not('status', 'in', '("cancelled","completed")');
      if (error) {
        console.error('[useWindowOrderCounts] query error:', error.code, error.message);
        throw error;
      }
      // Aggregate in JS
      const map = new Map<string, { orders: number; items: number }>();
      for (const row of (data || []) as any[]) {
        const wid = row.pickup_window_id as string;
        if (!wid) continue;
        const entry = map.get(wid) || { orders: 0, items: 0 };
        entry.orders += 1;
        const itemQty = ((row.order_items || []) as { quantity: number }[])
          .reduce((s: number, i: { quantity: number }) => s + (i.quantity || 0), 0);
        entry.items += itemQty;
        map.set(wid, entry);
      }
      return Array.from(map.entries()).map(([wid, v]) => ({
        pickup_window_id: wid,
        order_count: v.orders,
        total_items: v.items,
      }));
    },
    enabled: windowIds.length > 0,
  });
}

/** Format a pickup window as a Swedish label, e.g. "Lördag 14 mars · 10:00–14:00" */
export function formatPickupWindowLabel(w: PickupWindow | PublicPickupWindow): string {
  const d = new Date(w.pickup_date + 'T00:00:00');
  const dayName = d.toLocaleDateString('sv-SE', { weekday: 'long' });
  const dateStr = d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' });
  const capitalDay = dayName.charAt(0).toUpperCase() + dayName.slice(1);
  const start = w.pickup_start.slice(0, 5);
  const end = w.pickup_end.slice(0, 5);
  return `${capitalDay} ${dateStr} · ${start}–${end}`;
}
